import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { gql, useQuery } from '@apollo/client';

import { useSites } from 'store'

import { useFilter } from './store'
import { DEVICE_STATUSES, getDeviceStatus } from './device-status'
import type { DeviceStatus } from './device-status'

export interface Device {
  uuid: string;
  kind: 'tv' | 'chromecast';
  mac?: string;
  standalone?: boolean;
  is_provisioning: boolean;
  is_reachable: boolean;
  serial: string;
  room: string;
  sub_room?: string;
}

export interface Room {
  id: string;
  name: string;
  wifi_psk: string;
  ssid_name: string;
  devices: Device[];
  site_sf_id: string;
  is_iot_format: boolean;
}

const OPERATION_FIND_ROOMS = gql`
  query GetRooms($sf_ids: String!) {
    rooms: findRooms(
      sf_ids: $sf_ids
      withTV: true
      withChromecast: true
    ) {
      id
      name
      hotspot_id
      site_sf_id
      site_name
      ssid_name
      wifi_psk
      tvs_pincode
      is_iot_format
      devices {
        ... on TV {
          uuid
          label
          model
          model_type
          manufacturer  
          serial
          mac
          psk  
          is_provisioning
          is_reachable
          kind
          wired
          room
          sub_room
        }
        ... on Chromecast {
          uuid
          label
          model
          model_type
          manufacturer  
          serial
          mac
          psk  
          is_provisioning
          is_reachable
          kind
          standalone
        }
      }
    }
  }
`

interface DeviceCounts {
  tv: number;
  chromecast: number;
  operational: number;
  unreachable: number;
  not_configured: number;
}

const useRooms = (): {rooms: Room[], loading: boolean, deviceCounts: DeviceCounts} => {
  const [{ selected }] = useSites()
  const sfIds = useMemo(
    () => selected ? selected.map(({ sf_id }) => sf_id).join(',') : '',
    [selected],
  )
  const [filter] = useFilter()
  const { verbatim } = filter
  const deviceTypes: Device['kind'][] = filter.deviceTypes
  const statuses: DeviceStatus[] = filter.statuses
  const { data, loading } = useQuery(OPERATION_FIND_ROOMS, {
    variables: {
      sf_ids: sfIds,
    }
  })

  const rooms = useMemo(() => {
    if (!data) return []
    return data.rooms.filter(room => {
      if (verbatim.length > 0 && !room.name.toLowerCase().includes(verbatim.toLowerCase())) {
        return false
      }
      if (deviceTypes.length === 0 && statuses.length === 0) return true

      return room.devices.some(device => (
        (deviceTypes.length === 0 || deviceTypes.includes(device.kind)) &&
        (statuses.length === 0 || statuses.includes(getDeviceStatus(device)))
      ))
    })

  }, [data, deviceTypes, statuses, verbatim])

  const deviceCounts = useMemo(() => {
    const counts: DeviceCounts = {
      tv: 0,
      chromecast: 0,
      [DEVICE_STATUSES.OPERATIONAL]: 0,
      [DEVICE_STATUSES.UNREACHABLE]: 0,
      [DEVICE_STATUSES.NOT_CONFIGURED]: 0
    }

    data?.rooms.forEach(room => room.devices.forEach(device => {
      counts[device.kind] += 1
      counts[getDeviceStatus(device)] += 1
    }))

    return counts
  }, [data])
  
  return {
    rooms,
    deviceCounts,
    loading,
  }
}

export const useRoom = (): Room | undefined => {
  const { rooms } = useRooms()
  const { id } = useParams()
  
  return useMemo(() => rooms.find((item) => item.id === id), [rooms, id])
}

export default useRooms